
import { mapState, mapActions } from 'vuex';
import { formatCurrency } from '@/utils/currency';
import FSlider from '@/components/atoms/forms/h-slider';
import FInfoTooltip from '@/components/molecules/tooltips/info-tooltip';
import PlaygroundCta from '@/ui/retirement-calculator/playground/playground-cta.vue';
import Highchart from '@/components/highchart-new';

export default {
  name: 'PensionGapPieChart',

  components: {
    Cta: PlaygroundCta,
    FInfoTooltip,
    FSlider,
    Highchart,
  },

  props: {
    isLoading: Boolean,
    showCta: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      sum: null,
      pensionGapColor: '#E27265',
      publicPensionColor: this.$colors.primary.lighten1,
      pensionIncomeColor: this.$colors.blue.base,
      isMounted: false,
      isChartMounted: true,
    };
  },

  computed: {
    ...mapState({
      gap: (state) => state['pension-calculator'].outputs.pie_chart_pension_gap,
      expectedPublicPension: (state) =>
        state['pension-calculator'].outputs.pie_chart_grv_pension,
      optimalPension: (state) =>
        state['pension-calculator'].outputs.pie_chart_required_pension,
      investmentAndOtherPensionIncome: (state) =>
        state['pension-calculator'].outputs
          .pie_chart_own_investment_other_pension_income,
      monthlyInvestmentRecommend: (state) =>
        state['pension-calculator'].outputs.slider_monthly_investment_recommend,
      maxAge: (state) =>
        state['pension-calculator'].outputs.slider_retirement_age_max,
      minAge: (state) =>
        state['pension-calculator'].outputs.slider_retirement_age_min,
      withInflation: (state) => state['pension-calculator'].inputs.inflation,

      inflationInfoText() {
        const hasGap = this.gap > 0;
        const translationKey = hasGap ? 'gapInfo.withGap' : 'gapInfo.noGap';

        return this.$t(translationKey, {
          pensionGap: formatCurrency(this.gap),
          monthlyInvestment: formatCurrency(this.monthlyInvestmentRecommend),
        });
      },

      ctaButtonText() {
        const hasGap = this.gap > 0;
        const translationKey = hasGap ? 'ctaText.withGap' : 'ctaText.noGap';
        return this.$t(translationKey);
      },

      isExampleData: (state) => state['pension-calculator'].isExampleData,
    }),

    shouldShowSkeletons() {
      return this.isLoading || !this.isMounted;
    },

    pieCenterTextStyle() {
      const left = this.$vuetify.breakpoint.lg ? 30 : 25;
      const height = this.hasLegend ? 270 : 450;
      return `height: ${height}px; left: ${left}px`;
    },

    retirementAge: {
      get() {
        return this.$store.state['pension-calculator'].inputs
          .slider_desired_retirement_age;
      },
      set(value) {
        return this.$store.commit('pension-calculator/setInputProperty', {
          property: 'slider_desired_retirement_age',
          value,
        });
      },
    },

    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    hasLegend() {
      return this.$vuetify.breakpoint.mdAndDown;
    },

    subtitleText() {
      const { retirementAge } = this;
      const optimalPension = formatCurrency(this.optimalPension);
      const gap = formatCurrency(this.gap);

      const subtitle = this.$t('results.subtitle', {
        gap,
        optimalPension,
        retirementAge,
      });

      return !this.isExampleData
        ? subtitle
        : `${this.$t('results.example')} ${subtitle}`;
    },

    // Chart config
    piePlotOptions() {
      const hasDataLabels = this.$vuetify.breakpoint.lgAndUp;
      const formatter = (chart) => {
        const isGap = chart.key === this.$t('gap');
        const isPPP = chart.key === this.$t('publicPension');
        const textClasses = isGap ? 'text-body-3' : 'text-body-4';

        let colour = 'blue--text';
        if (isGap) colour = 'error--text text--darken-1';
        if (isPPP) colour = 'primary--text text--lighten-1';

        const numberSize = isGap ? 'text-body-2' : 'h3';
        const weight = isGap ? 'font-weight-bold' : '';

        return `
          <p class="${textClasses}">${chart.key}</p>
          <br/><br/>
          <p class="${numberSize} ${colour} ${weight}">${formatCurrency(
          chart.y,
        )}€</p>
        `;
      };

      return {
        pie: {
          allowPointSelect: false,
          innerSize: '85%',
          minSize: '85%',
          size: '85%',
          showInLegend: true,
          dataLabels: {
            enabled: hasDataLabels,
            connectorWidth: 0,
            followPointer: false,
            followTouchMove: false,
            distance: 6,
            formatter() {
              return formatter(this);
            },
          },
        },
      };
    },
    seriesData() {
      return [
        {
          data: [
            {
              name: this.$t('gap'),
              tip: this.$t('tooltips.gap'),
              y: this.gap,
            },
            {
              name: this.$t('pensionIncome'),
              tip: this.$t('tooltips.investmentAndOtherPensionIncome'),
              y: this.investmentAndOtherPensionIncome,
            },
            {
              name: this.$t('publicPension'),
              tip: this.$t('tooltips.expectedPublicPension'),
              y: this.expectedPublicPension,
            },
          ],
        },
      ];
    },
    chartConfig() {
      return {
        chart: {
          plotBorderWidth: null,
          height: this.hasLegend ? 370 : 450,
          type: 'pie',
          style: {
            fontFamily: 'TTNorms',
            fontSize: 12,
          },
        },
        colors: [
          this.pensionGapColor,
          this.pensionIncomeColor,
          this.publicPensionColor,
        ],
        legend: {
          enabled: this.hasLegend,
          align: 'center',
          borderWidth: 0,
          itemStyle: {
            '-moz-osx-font-smoothing': 'grayscale',
            '-webkit-font-smoothing': 'antialiased',
            color: this.$colors.secondary.base,
            cursor: 'default',
            fontWeight: 400,
          },
          margin: 10,
          itemDistance: 25,
          itemMarginBottom: 8,
          itemMarginTop: 8,
          padding: 0,
          labelFormatter() {
            return `${this.name.replace(
              '<br>',
              ' ',
            )} <span class="highchart__legendValue">${formatCurrency(
              this.y,
            )} €</span>`;
          },
        },
        credits: {
          enabled: false,
        },
        plotOptions: this.piePlotOptions,
        title: false,
        tooltip: {
          formatter() {
            return `
              <div class="tooltip tooltip--dark">
                <div class="tooltip__body">
                  <div class="tooltip__item">
                    <span class="tooltip__item__name">${this.point.tip}</span>
                  </div>
                </div>
              </div>
            `;
          },
          useHTML: true,
        },
        series: this.seriesData,
      };
    },
  },

  mounted() {
    // TODO: the loaders are not showing while mounting
    this.isMounted = true;
  },

  methods: {
    ...mapActions({
      reverseInflationChoice: 'pension-calculator/reverseInflationChoice',
    }),

    scrollToPlayground() {
      this.$vuetify.goTo('#pension-playground', {
        duration: 500,
        offset: 0,
        easing: 'easeInOutCubic',
      });
    },
  },
};
